<script setup lang="ts">
import { getFilterDtwinRangeOptionValue } from "../utils/getFilterDtwinRangeOptionValue";
import { getFilterDtwinTypeValueFromRangeOptionValue } from "../utils/getFilterDtwinTypeValueFromRangeOptionValue";
import LxcCheckbox from "@lxc/app-device-common/src/components/form/LxcCheckbox.vue";
import { type Ref } from "vue";
import type { DtwinTypeOption } from "~/modules/dtwin/models/DtwinTypeOptions.interface";
import { getFilterDtwinTypeOptionValue } from "~/modules/dtwin/utils/getFilterDtwinTypeOptionValue";
import { getFilterDtwinTypeValuesFromOption } from "~/modules/dtwin/utils/getFilterDtwinTypeValuesFromOption";

const types: Ref<string[]> = defineModel<string[]>("types", {
  required: true,
});

const ranges: Ref<string[]> = defineModel<string[]>("ranges", {
  required: true,
});

const props = defineProps<{
  typeOptions: DtwinTypeOption[];
}>();

const dtwinTypeOptions: ComputedRef<DtwinTypeOption[]> = computed(() => {
  return props.typeOptions
    .map((dtwinTypeOption) => {
      const dtwinTypeOptionValue =
        getFilterDtwinTypeOptionValue(dtwinTypeOption);

      return {
        ...dtwinTypeOption,
        value: dtwinTypeOptionValue,
        ranges: dtwinTypeOption.ranges
          ?.map((rangeOption) => {
            return {
              ...rangeOption,
              disabled:
                rangeOption.disabled ||
                // only enable the range options if there is no type selected
                // or if the only one type selected is the type of the ranges
                !(
                  selectedTypes.value.length === 0 ||
                  (selectedTypes.value.length === 1 &&
                    selectedTypes.value.includes(dtwinTypeOptionValue))
                ),
            };
          })
          .sort((option1, option2) =>
            option1.label.localeCompare(option2.label),
          ),
        // disable the type options if at least one range is selected whatever the range
        // because the only type of the selected range can be selected
        disabled: ranges.value.length > 0,
      };
    })
    .sort((option1, option2) => option1.label.localeCompare(option2.label));
});

const selectedTypes = computed({
  get() {
    return props.typeOptions
      .filter((option) => {
        const values = Array.isArray(option.value)
          ? option.value
          : [option.value];
        return values.some((v) => types.value.includes(v));
      })
      .map((option) => getFilterDtwinTypeOptionValue(option));
  },
  set(selected: string[]) {
    types.value = selected.flatMap((option) =>
      getFilterDtwinTypeValuesFromOption(option),
    );
  },
});

function onUpdateRange(ranges: string[]) {
  for (const range of ranges) {
    const type = getFilterDtwinTypeValueFromRangeOptionValue(range);
    if (!selectedTypes.value.includes(type)) {
      selectedTypes.value = selectedTypes.value.concat(type);
    }
  }
}
</script>

<template>
  <div class="bg-white p-6">
    <fieldset v-for="item in dtwinTypeOptions" :key="item.value as string">
      <lxc-checkbox
        v-model="selectedTypes"
        :value="item.value"
        :label="item.label"
        :name="item.name"
        :disabled="item.disabled"
        class="mb-2"
      />
      <div
        v-if="item.ranges?.length && item.ranges?.length > 0"
        class="ml-6 my-4"
      >
        <lxc-checkbox
          v-for="range in item.ranges"
          :key="
            getFilterDtwinRangeOptionValue(
              item.value as string,
              range.value as string,
            )
          "
          v-model="ranges"
          :value="
            getFilterDtwinRangeOptionValue(
              item.value as string,
              range.value as string,
            )
          "
          :label="range.label"
          :name="`${item.value}[]`"
          :disabled="range.disabled"
          @update:model-value="onUpdateRange"
        />
      </div>
    </fieldset>
  </div>
</template>
